
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { products } from '../../mocks/products';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const filtered = query.trim().length > 0
    ? products.filter(
        (p) =>
          p.name.toLowerCase().includes(query.toLowerCase()) ||
          p.category.toLowerCase().includes(query.toLowerCase()) ||
          p.description.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.style.overflow = 'hidden';
    } else {
      setQuery('');
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleProductClick = (id: number) => {
    onClose();
    navigate(`/catalogo`);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-fade-in">
        {/* Search input */}
        <div className="flex items-center gap-4 px-6 py-5 border-b border-gray-100">
          <span className="w-6 h-6 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-xl"></i>
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar productos..."
            className="flex-1 text-base text-gray-800 placeholder-gray-400 outline-none bg-transparent"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          )}
          <button
            onClick={onClose}
            className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer transition-colors whitespace-nowrap ml-2"
          >
            Cerrar
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[420px] overflow-y-auto">
          {query.trim().length === 0 && (
            <div className="px-6 py-10 text-center">
              <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-gray-300">
                <i className="ri-search-line text-4xl"></i>
              </span>
              <p className="text-gray-400 text-sm">Escribí el nombre o categoría del producto</p>
            </div>
          )}

          {query.trim().length > 0 && filtered.length === 0 && (
            <div className="px-6 py-10 text-center">
              <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-gray-300">
                <i className="ri-emotion-sad-line text-4xl"></i>
              </span>
              <p className="text-gray-500 text-sm">No encontramos productos para <strong>"{query}"</strong></p>
              <button
                onClick={() => { onClose(); navigate('/catalogo'); }}
                className="mt-4 px-5 py-2 bg-[#F2C4CE] text-gray-800 rounded-full text-sm hover:bg-[#e8adb8] transition-colors cursor-pointer whitespace-nowrap"
              >
                Ver todo el catálogo
              </button>
            </div>
          )}

          {filtered.length > 0 && (
            <div className="py-2">
              {filtered.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleProductClick(product.id)}
                  className="w-full flex items-center gap-4 px-6 py-4 hover:bg-[#F2C4CE]/15 transition-colors cursor-pointer text-left"
                >
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{product.category}</p>
                  </div>
                  <span className="text-sm font-light text-gray-700 whitespace-nowrap">${product.price}</span>
                </button>
              ))}
              <div className="px-6 py-3 border-t border-gray-100">
                <button
                  onClick={() => { onClose(); navigate('/catalogo'); }}
                  className="w-full text-center text-sm text-[#c9909a] hover:text-[#b07880] transition-colors cursor-pointer py-1"
                >
                  Ver todos los productos →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
